import type { CarId, ViewerState } from "./protocol";
import { GOAL_RACE_TIME_MS } from "./race-screen-contract";

export const RACE_RELOAD_STATE_KEY = "superformula-race-reload-state/v1";

interface RaceReloadCheckpoint {
  schema_version: "superformula-race-reload-state/v1";
  car_id: CarId;
  race_time_ms: number;
  seek_generation: number;
}

type ReloadStorage = Pick<Storage, "getItem" | "setItem">;

const isCarId = (value: unknown): value is CarId =>
  value === "car1" || value === "car6" || value === "car14";

const isCheckpoint = (value: unknown): value is RaceReloadCheckpoint => {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<RaceReloadCheckpoint>;
  return item.schema_version === "superformula-race-reload-state/v1"
    && isCarId(item.car_id)
    && Number.isInteger(item.race_time_ms)
    && Number(item.race_time_ms) >= 0
    && Number(item.race_time_ms) <= GOAL_RACE_TIME_MS[item.car_id]
    && Number.isSafeInteger(item.seek_generation)
    && Number(item.seek_generation) >= 0
    && Number(item.seek_generation) < Number.MAX_SAFE_INTEGER;
};

export function loadRaceReloadState(storage: ReloadStorage, fallback: ViewerState): ViewerState {
  try {
    const serialized = storage.getItem(RACE_RELOAD_STATE_KEY);
    if (!serialized) return fallback;
    const checkpoint = JSON.parse(serialized) as unknown;
    if (!isCheckpoint(checkpoint)) return fallback;
    return {
      carId: checkpoint.car_id,
      raceTimeMs: checkpoint.race_time_ms,
      seekGeneration: checkpoint.seek_generation + 1,
      playState: "paused",
      topologyHash: fallback.topologyHash,
    };
  } catch {
    return fallback;
  }
}

export function saveRaceReloadState(storage: ReloadStorage, state: ViewerState): boolean {
  if (!isCarId(state.carId)
    || !Number.isInteger(state.raceTimeMs)
    || state.raceTimeMs < 0
    || state.raceTimeMs > GOAL_RACE_TIME_MS[state.carId]
    || !Number.isSafeInteger(state.seekGeneration)
    || state.seekGeneration < 0) return false;
  const checkpoint: RaceReloadCheckpoint = {
    schema_version: "superformula-race-reload-state/v1",
    car_id: state.carId,
    race_time_ms: state.raceTimeMs,
    seek_generation: state.seekGeneration,
  };
  try {
    storage.setItem(RACE_RELOAD_STATE_KEY, JSON.stringify(checkpoint));
    return true;
  } catch {
    return false;
  }
}
